import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, FileText, Scale, Shield, Phone, CheckCircle } from 'lucide-react';
import SectionTitle from '../components/ui/SectionTitle';
import Button from '../components/ui/Button';
import WhatsAppFloatingButton from '../components/layout/WhatsAppFloatingButton';
import { 
  category1Documents, 
  category2Documents, 
  litigationServices, 
  nonLitigationServices 
} from '../data/documents.js';
import { contactInfo } from '../data/contactInfo';

// Import local image
import documentationImg from '../assets/documentation.png';

// Hero Section Component
const HeroSection = () => {
  return (
    <section className="relative bg-primary-blue min-h-[35vh] flex items-center">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-60"
        style={{ backgroundImage: `url(${documentationImg})` }}
      ></div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center text-center">
          <div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-2">Documentation Services</h1>
            <p className="text-xl text-gray-300">Expert assistance for all your legal documentation needs</p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Document Card Component with Icon and WhatsApp Button
const DocumentCard = ({ document, icon: Icon }) => {
  const whatsappMessage = `Hi, I'm interested in ${document.name}. Please provide more information.`;
  const whatsappUrl = `https://wa.me/${contactInfo.phone.primaryLink}?text=${encodeURIComponent(whatsappMessage)}`;
  
  return (
    <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100 hover:shadow-lg transition-all duration-300 text-center">
      <div className="flex flex-col items-center">
        <div className="w-14 h-14 bg-gold/10 rounded-full flex items-center justify-center mb-4">
          <Icon className="w-7 h-7 text-gold" />
        </div>
        <h4 className="font-semibold text-primary-blue text-lg mb-4">{document.name}</h4>
        <a
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center bg-green-500 hover:bg-green-600 text-white font-medium py-2 px-4 rounded-lg text-sm transition-colors"
        >
          <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884" />
          </svg>
          Enquire Now
        </a>
      </div>
    </div>
  );
};

// Category Section Component
const CategorySection = ({ title, description, documents, bgColor = "bg-gray-50" }) => {
  return (
    <section className={`py-16 ${bgColor}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-primary-blue">{title}</h2>
          <p className="text-dark-gray mt-2">{description}</p>
        </div>
        
<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {documents.map((doc) => (
            <DocumentCard key={doc.id} document={doc} icon={doc.icon} />
          ))}
        </div>
      </div>
    </section>
  );
};

// Legal Service Column Component
const LegalServiceColumn = ({ title, services, color }) => {
  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
      <div className={`p-6 ${color === 'blue' ? 'bg-primary-blue' : 'bg-secondary-blue'}`}>
        <h3 className="text-2xl font-bold text-white">{title}</h3>
      </div>
      <div className="p-6">
        <ul className="space-y-3">
          {services.map((service) => (
            <li key={service.id} className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
              <div>
                <span className="font-medium text-dark-gray">{service.name}</span>
                <p className="text-sm text-gray-500">{service.description}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

// CTA Section
const CTASection = () => {
  return (
    <section className="py-20 bg-gold">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="text-3xl md:text-4xl font-bold text-primary-blue mb-4">
          Need Help with Your Documentation?
        </h2>
        <p className="text-xl text-primary-blue mb-8">
          Our expert team is ready to assist you with all your legal documentation needs.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/contact"
            className="inline-flex items-center justify-center bg-primary-blue hover:bg-secondary-blue text-white font-semibold py-4 px-8 rounded-lg transition-all duration-200 transform hover:scale-105 group"
          >
            Get Free Consultation
            <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
          </Link>
          <a
            href={`tel:${contactInfo.phone.primary}`}
            className="inline-flex items-center justify-center bg-white hover:bg-gray-100 text-primary-blue font-semibold py-4 px-8 rounded-lg transition-all duration-200 transform hover:scale-105 group"
          >
            <Phone className="w-5 h-5 mr-2" />
            Call Now
          </a>
        </div>
      </div>
    </section>
  );
};

const Documentations = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <HeroSection />

      {/* Category 1 - Government Certificate Services */}
      <CategorySection 
        title="Government Certificate Services"
        description="Official certificates and licenses from government authorities"
        documents={category1Documents}
        bgColor="bg-gray-50"
      />

      {/* Category 2 - Name Change & Registration Services */}
      <CategorySection 
        title="Name Change & Registration Services"
        description="Name changes and registration services for various documents"
        documents={category2Documents}
        bgColor="bg-white"
      />

      {/* Legal Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionTitle
            subtitle="Legal Expertise"
            title="Legal Services"
            alignment="center"
            showLine={true}
          />
          
          <p className="text-center text-dark-gray text-lg max-w-3xl mx-auto mb-12">
            Our experienced legal team provides expert representation and consultation in both litigation 
            and non-litigation matters across various domains of law.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <LegalServiceColumn 
              title="Litigation Services"
              services={litigationServices}
              color="blue"
            />
            <LegalServiceColumn 
              title="Non-Litigation Services"
              services={nonLitigationServices}
              color="dark"
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection />

      {/* Floating WhatsApp Button */}
      <WhatsAppFloatingButton />
    </div>
  );
};

export default Documentations;

